import { isString } from 'lodash-es';
import { DateTime, Duration } from 'luxon';
import type { FunctionModifier, Modifier } from 'react-day-picker';
import type { StartOfWeek } from 'types/account';
import {
  generateDays,
  getDayRangeInMonth,
  getDayRangeInWeek,
  getDayRangeInYear,
  getFirstDayOfWeek,
  getFirstDayOfWeekCore,
  getLastDayOfWeek,
  getNativeDateFromString,
  isAfter,
  isBefore,
  isBetweenDates,
  isSameDay,
  isSameMonth,
  isSameWeek,
  isSameYear,
} from 'utils/date-helper';
import { fromISO, fromJSDate, interval } from 'utils/luxon-helpers';
import { isWorkDay } from 'utils/work-days';
import type {
  DatePickerConfig,
  DatePickerMode,
  DateRange,
  MaxRange,
  RelativeDateRange,
} from './types';

interface SelectionProps {
  maxRange?: MaxRange;
  day: Date | string;
  dateRange?: DateRange;
}
// based on the number of remaining available days and the current selection, is this day enabled?
export const isDateDisabledForSelection = (props: SelectionProps) => {
  const { maxRange, day, dateRange } = props;
  const dtDay = isString(day) ? fromISO(day).startOf('day') : fromJSDate(day).startOf('day');

  if (!dateRange || !maxRange) {
    return false;
  }
  const from = fromISO(dateRange.from).startOf('day');
  const rangeLength = Duration.fromObject({ [maxRange.type]: maxRange.value });

  return Math.abs(dtDay.diff(from, 'days').days) + 1 > rangeLength.as('days');
};

export function getUnitComparisonFn(mode: DatePickerMode) {
  if (mode === 'week') {
    return isSameWeek;
  }
  if (mode === 'month') {
    return isSameMonth;
  }
  if (mode === 'year') {
    return isSameYear;
  }
  return isSameDay;
}

interface DisabledOptions {
  disabledBefore?: string;
  disabledAfter?: string;
  disableNonWorkDays?: boolean;
  dateRange: DateRange;
  maxRange?: MaxRange;
}

export function handleDisabledDays(options: DisabledOptions): FunctionModifier {
  const { disabledBefore, disabledAfter, disableNonWorkDays, dateRange, maxRange } = options;
  if (disabledBefore && disabledAfter) {
    return (day: Date) => {
      const dayString = fromJSDate(day).toISODate();
      const isOutOfBounds =
        isBefore(dayString, disabledBefore) || isAfter(dayString, disabledAfter);
      return (
        isOutOfBounds ||
        !!(disableNonWorkDays && !isWorkDay(dayString)) ||
        isDateDisabledForSelection({ day, maxRange, dateRange })
      );
    };
  }
  if (disableNonWorkDays && disabledBefore) {
    return (day: Date) => {
      const dayString = fromJSDate(day).toISODate();
      return !!(
        !isWorkDay(dayString) ||
        isBefore(dayString, disabledBefore) ||
        isDateDisabledForSelection({ day, maxRange, dateRange })
      );
    };
  }
  if (disableNonWorkDays && disabledAfter) {
    return (day: Date) =>
      !!(
        !isWorkDay(day) ||
        isAfter(fromJSDate(day).toISODate(), disabledAfter) ||
        isDateDisabledForSelection({ day, maxRange, dateRange })
      );
  }
  if (disableNonWorkDays) {
    return (day: Date) =>
      !isWorkDay(day) || isDateDisabledForSelection({ day, maxRange, dateRange });
  }
  if (disabledBefore) {
    return (day: Date) =>
      !!(
        isBefore(fromJSDate(day).toISODate(), disabledBefore) ||
        isDateDisabledForSelection({ day, maxRange, dateRange })
      );
  }
  if (disabledAfter) {
    return (day: Date) =>
      !!(
        isAfter(fromJSDate(day).toISODate(), disabledAfter) ||
        isDateDisabledForSelection({ day, maxRange, dateRange })
      );
  }

  return (day: Date) => isDateDisabledForSelection({ day, maxRange, dateRange });
}

// make sure our dates are correct for the given mode
export function sanitizeRange(
  baseDate: string,
  mode: DatePickerMode,
  startOfWeek: StartOfWeek,
  dateRange?: DateRange
) {
  if (dateRange) {
    // For weeks, we need to calculate the first and last day of the week
    if (mode === 'week') {
      return {
        from: getFirstDayOfWeek(dateRange.from, startOfWeek),
        to: dateRange.to ? getLastDayOfWeek(dateRange.to, startOfWeek) : undefined,
      };
    }

    // For other modes, use the regular Luxon startOf/endOf
    return {
      from: fromISO(dateRange.from).startOf(mode).toISODate(),
      to: dateRange.to ? fromISO(dateRange.to).endOf(mode).toISODate() : undefined,
    };
  }

  // If no date range is provided, calculate based on baseDate
  const isoBaseDate = fromISO(baseDate);

  if (mode === 'week') {
    return {
      from: getFirstDayOfWeek(baseDate, startOfWeek),
      to: getLastDayOfWeek(baseDate, startOfWeek),
    };
  }

  return {
    from: isoBaseDate.startOf(mode).toISODate(),
    to: isoBaseDate.endOf(mode).toISODate(),
  };
}

export function getModifiers(
  baseDate: string,
  dateRange: DateRange | undefined,
  entered: { enteredTo: string | null; enteredWeek: string | null },
  mode: DatePickerMode,
  startOfWeek: StartOfWeek
) {
  const { enteredTo, enteredWeek } = entered;
  const { from, to } = sanitizeRange(baseDate, mode, startOfWeek, dateRange);
  const hoveredWeekMarker: Array<Modifier> = [];

  if (mode === 'week') {
    // For week mode, we want to ensure we highlight the currently selected week
    const isRangeSelection = to && !isSameWeek(from, to, startOfWeek);

    // Get the start and end of the selected week
    const selectedWeekStart = getFirstDayOfWeek(from, startOfWeek);
    const selectedWeekEnd = getLastDayOfWeek(from, startOfWeek);

    const range = {
      start: isRangeSelection
        ? getNativeDateFromString(from)
        : getNativeDateFromString(selectedWeekStart),
      end: isRangeSelection
        ? getNativeDateFromString(to!)
        : getNativeDateFromString(selectedWeekEnd),
    };

    const hoveredWeekStart = enteredWeek ? getFirstDayOfWeek(enteredWeek, startOfWeek) : null;

    const hoveredWeekInterval =
      hoveredWeekStart && enteredWeek && !isSameMonth(hoveredWeekStart, enteredWeek)
        ? interval(fromISO(hoveredWeekStart), fromISO(enteredWeek))
        : null;

    if (hoveredWeekInterval) {
      for (const day of generateDays(hoveredWeekInterval)) {
        hoveredWeekMarker.push(day.toJSDate());
      }
    }
    return { ...range, hoveredWeekMarker };
  }

  return {
    start:
      enteredTo && enteredTo < from
        ? getNativeDateFromString(enteredTo)
        : getNativeDateFromString(from),
    end: !enteredTo
      ? getNativeDateFromString(to ?? from)
      : enteredTo < from
        ? getNativeDateFromString(from)
        : getNativeDateFromString(enteredTo),
    hoveredWeekMarker: [],
  };
}

export function getSelectedDays(
  baseDate: string,
  day: string,
  enteredTo: string | null,
  mode: DatePickerMode,
  startOfWeek: StartOfWeek,
  dateRange?: DateRange
) {
  const { from, to } = sanitizeRange(baseDate, mode, startOfWeek, dateRange);

  // For week mode, we want to highlight the week(s) that contain the selected day(s)
  if (mode === 'week') {
    // If we have a range (to is defined) and we're hovering (enteredTo is defined)
    if (to && enteredTo) {
      // If the enteredTo date is before the from date, highlight from enteredTo to to
      if (enteredTo < from) {
        return isBetweenDates(day, getFirstDayOfWeek(enteredTo, startOfWeek), to);
      }
      // Otherwise highlight from from to enteredTo
      return isBetweenDates(day, from, getLastDayOfWeek(enteredTo, startOfWeek));
    }

    // If we have a range but no hover (enteredTo is null)
    if (to) {
      return isBetweenDates(day, from, to);
    }

    // For single week selection, highlight the week containing the from date
    const selectedWeekStart = getFirstDayOfWeek(from, startOfWeek);
    const selectedWeekEnd = getLastDayOfWeek(from, startOfWeek);
    return isBetweenDates(day, selectedWeekStart, selectedWeekEnd);
  }

  const rangeEnd = to || enteredTo;
  const range = rangeEnd ? setDatesInCorrectOrder({ from, to: rangeEnd }) : { from, to: from };

  return isBetweenDates(day, range.from, range.to!);
}

export function addUnitToRange(
  day: string,
  compareFn: (date: string, compareDate: string, startOfWeek: StartOfWeek) => boolean,
  startOfWeek: StartOfWeek,
  dateRange?: DateRange
): { from: string; to: string } {
  const { from, to } = dateRange || {};
  if (!from) {
    return { from: day, to: day };
  }
  if (!to && compareFn(from, day, startOfWeek)) {
    return { from, to: from };
  }
  if (!to && isBefore(day, from)) {
    return { from: day, to: from };
  }
  if (!to) {
    return { from, to: day };
  }
  if (compareFn(to, day, startOfWeek) && compareFn(from, day, startOfWeek)) {
    return { from, to };
  }
  if (compareFn(to, day, startOfWeek)) {
    return { from: to, to };
  }
  if (compareFn(from, day, startOfWeek)) {
    return { from, to: from };
  }
  if (isAfter(from, day)) {
    return { from: day, to };
  }
  return { from, to: day };
}

export function getSelectedDatesForMode(
  day: Date,
  mode: DatePickerMode,
  startOfWeek: StartOfWeek,
  dateRange?: DateRange
) {
  const dayString = fromJSDate(day).toISODate();

  // We'll keep the original day as the date, but calculate the appropriate range
  const date = dayString;

  if (dateRange) {
    if (dateRange.from && dateRange.to) {
      return {
        from:
          mode === 'week'
            ? getFirstDayOfWeek(date, startOfWeek)
            : fromISO(date).startOf(mode).toISODate(),
        to: undefined,
      };
    }

    const buildRange = addUnitToRange(date, getUnitComparisonFn(mode), startOfWeek, dateRange);
    const range = setDatesInCorrectOrder(buildRange);

    return {
      from:
        mode === 'week'
          ? getFirstDayOfWeek(range.from, startOfWeek)
          : fromISO(range.from).startOf(mode).toISODate(),
      to:
        mode === 'week'
          ? getLastDayOfWeek(range.to!, startOfWeek)
          : range.to
            ? fromISO(range.to).endOf(mode).toISODate()
            : undefined,
    };
  } else {
    return mode === 'week'
      ? getDayRangeInWeek(date, startOfWeek)
      : mode === 'month'
        ? getDayRangeInMonth(date)
        : mode === 'year'
          ? getDayRangeInYear(date)
          : { from: date, to: date };
  }
}

// make sure from comes before to, mainly used in range mode when previewing selections
export function setDatesInCorrectOrder(range: DateRange) {
  if (!range.to || range.to > range.from) {
    return range;
  }
  return { from: range.to, to: range.from };
}

export const datePickerConfiguration: Record<DatePickerMode, DatePickerConfig> = {
  year: {
    rowHeight: 33,
    rowLength: 5,
    scrollStep: 1,
  },
  month: {
    rowHeight: 106,
    scrollStep: 6,
  },
  week: {
    rowHeight: 185,
    scrollStep: 6,
  },
  day: {
    rowHeight: 185,
    scrollStep: 6,
  },
};

export const relativeDateRanges: { label: string; value: RelativeDateRange }[] = [
  { label: 'This week', value: 'this_week' },
  { label: 'Previous week', value: 'prev_week' },
  { label: 'This month', value: 'this_month' },
  { label: 'Previous month', value: 'prev_month' },
  { label: 'This quarter', value: 'this_quarter' },
  { label: 'Previous quarter', value: 'prev_quarter' },
  { label: 'This year', value: 'this_year' },
  { label: 'Previous year', value: 'prev_year' },
];

export const convertRelativeDateRangeToFixedDateRange = (
  relativeDateRange: RelativeDateRange,
  firstDayOfWeek: StartOfWeek
): DateRange => {
  const now = DateTime.now();
  switch (relativeDateRange) {
    case 'this_week': {
      const startOfWeek = getFirstDayOfWeekCore(now, firstDayOfWeek);
      const endOfWeek = startOfWeek.plus({ days: 6 }).endOf('day');

      return {
        from: startOfWeek.toISO(),
        to: endOfWeek.toISO(),
      };
    }
    case 'prev_week': {
      const startOfCurrentWeek = getFirstDayOfWeekCore(now, firstDayOfWeek);
      const startOfPreviousWeek = startOfCurrentWeek.minus({ weeks: 1 });
      const endOfPreviousWeek = startOfPreviousWeek.plus({ days: 6 }).endOf('day');

      return {
        from: startOfPreviousWeek.toISO(),
        to: endOfPreviousWeek.toISO(),
      };
    }
    case 'this_month': {
      const startOfCurrentMonth = now.startOf('month');
      const endOfCurrentMonth = now.endOf('month');

      return {
        from: startOfCurrentMonth.toISO(),
        to: endOfCurrentMonth.toISO(),
      };
    }
    case 'prev_month': {
      const startOfPreviousMonth = now.minus({ months: 1 }).startOf('month');
      const endOfPreviousMonth = startOfPreviousMonth.endOf('month');

      return {
        from: startOfPreviousMonth.toISO(),
        to: endOfPreviousMonth.toISO(),
      };
    }
    case 'this_year': {
      const startOfCurrentYear = now.startOf('year');
      const endOfCurrentYear = now.endOf('year');

      return {
        from: startOfCurrentYear.toISO(),
        to: endOfCurrentYear.toISO(),
      };
    }
    case 'prev_year': {
      const startOfPreviousYear = now.minus({ year: 1 }).startOf('year');
      const endOfPreviousYear = startOfPreviousYear.endOf('year');

      return {
        from: startOfPreviousYear.toISO(),
        to: endOfPreviousYear.toISO(),
      };
    }
    case 'this_quarter': {
      const startOfCurrentQuarter = now.startOf('quarter');
      const endOfCurrentQuarter = now.endOf('quarter');

      return {
        from: startOfCurrentQuarter.toISO(),
        to: endOfCurrentQuarter.toISO(),
      };
    }
    case 'prev_quarter': {
      const startOfPreviousQuarter = now.minus({ quarter: 1 }).startOf('quarter');
      const endOfPreviousQuarter = startOfPreviousQuarter.endOf('quarter');

      return {
        from: startOfPreviousQuarter.toISO(),
        to: endOfPreviousQuarter.toISO(),
      };
    }
    default: {
      throw new Error(`Unexpected relativeDateRange value: ${relativeDateRange}`);
    }
  }
};

export const getSpanForRelativeDateRange = (
  relativeDateRange: RelativeDateRange
): 'day' | 'month' | 'year' => {
  if (relativeDateRange.endsWith('_week')) {
    return 'day';
  } else if (relativeDateRange.endsWith('_month') || relativeDateRange.endsWith('_quarter')) {
    return 'month';
  } else {
    return 'year';
  }
};
